import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Search, Bookmark, Zap, Target, CheckCircle2, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ChromeExtensionPrompt } from "@/components/ChromeExtensionPrompt";
import { useSmartApply } from "@/hooks/useSmartApply";
import { useNavigate } from "react-router-dom";
import { JobCard } from "@/components/JobCard";
import { JobDetailModal } from "@/components/JobDetailModal";
import { SelectedJob, useJobContext } from "@/contexts/JobContext";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { RecentEasyApplyJobs } from "@/components/RecentEasyApplyJobs";

interface ScrapedJob {
  id: string;
  external_job_id: string;
  title: string;
  company: string;
  location?: string;
  salary?: string;
  description?: string;
  job_url: string;
  apply_url?: string;
  posted_at?: string;
  source: string;
  apply_type?: string;
  logo?: string;
  contract_type?: string;
  work_type?: string;
}

export default function JobSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState("Remote");
  const [dateFilter, setDateFilter] = useState("any");
  const [typeFilter, setTypeFilter] = useState("any");
  const [jobs, setJobs] = useState<ScrapedJob[]>([]);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [selectedJobForModal, setSelectedJobForModal] = useState<SelectedJob | null>(null);
  const [showJobDetailModal, setShowJobDetailModal] = useState(false);
  const [appliedCount, setAppliedCount] = useState(0);
  const [readyCount, setReadyCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const { addToSmartApply } = useSmartApply();
  const { setSelectedJob } = useJobContext();
  const navigate = useNavigate();

  useEffect(() => {
    loadSavedJobs();
    fetchQueueStats();
  }, []);

  const fetchQueueStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [appliedResult, readyResult] = await Promise.all([
        supabase
          .from('smart_apply_queue')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'applied'),
        supabase
          .from('smart_apply_queue')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'ready')
      ]);

      setAppliedCount(appliedResult.count || 0);
      setReadyCount(readyResult.count || 0);
    } catch (error) {
      console.error('Error fetching queue stats:', error);
    }
  };

  const loadSavedJobs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from("saved_jobs")
      .select("job_id")
      .eq("user_id", user.id);

    if (data) {
      setSavedJobIds(new Set(data.map(j => j.job_id)));
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter job keywords");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    setSearchStatus("Checking for recent jobs...");
    
    // Update status if taking longer than 3 seconds
    const statusTimer = setTimeout(() => {
      setSearchStatus("Searching LinkedIn for Easy Apply jobs...");
    }, 3000);
    
    try {
      // Map UI filter values to API format for Apify
      const datePostedMap: Record<string, string> = {
        'any': 'Any time',
        '24h': 'Past 24 hours',
        'week': 'Past week',
        'month': 'Past month',
      };

      // Call live search edge function with all filters
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase.functions.invoke("search-jobs-live", {
        body: {
          userId: authUser?.id, // Pass user ID for job linking
          jobTitle: searchQuery,
          location: locationQuery || "Remote",
          datePosted: datePostedMap[dateFilter] || 'Any time',
          jobType: typeFilter === 'any' ? 'Any type' : typeFilter,
          limit: 50,
        },
      });

      clearTimeout(statusTimer);

      if (error) throw error;

      // Filters are now applied server-side via Apify API
      const allJobs = data.jobs || [];
      setJobs(allJobs);

      // Jobs are already linked by the edge function, no need to link again here
      // Just log the search history
      if (authUser && allJobs.length > 0) {
        const jobLinks = allJobs.map(job => ({
          user_id: authUser.id,
          job_id: job.id,
          search_query: searchQuery,
          search_location: locationQuery || "Remote",
        }));

        await supabase
          .from('user_searched_jobs')
          .upsert(jobLinks, { onConflict: 'user_id,job_id', ignoreDuplicates: true });

        // Log search history
        await supabase.from('user_search_history').insert({
          user_id: authUser.id,
          search_query: searchQuery,
          location: locationQuery || "Remote",
          job_type: typeFilter === 'any' ? null : typeFilter,
          date_posted: datePostedMap[dateFilter] || 'Any time',
          results_count: allJobs.length,
        });
      }

      // Show appropriate success message
      if (data.source === "cache") {
        toast.success(`Found ${allJobs.length} jobs from recent search`);
      } else if (data.source === "live") {
        toast.success(`Found ${allJobs.length} fresh Easy Apply jobs from LinkedIn!`, {
          description: `Scraped ${data.stats?.totalScraped || 0} jobs, filtered to ${data.stats?.easyApplyFound || 0} Easy Apply`,
        });
      } else if (data.source === "fallback") {
        toast.warning(`Showing ${allJobs.length} cached results (search had issues)`);
      }
      
      if (allJobs.length === 0) {
        toast.info("No Easy Apply jobs found. Try different keywords or filters.");
      }

      setSearchStatus("");
      
      // Reload recent jobs section to show updated list
      window.dispatchEvent(new CustomEvent('reloadRecentJobs'));
    } catch (error: any) {
      console.error("Job search error:", error);
      clearTimeout(statusTimer);
      setSearchStatus("");
      toast.error(error.message || "Failed to search jobs. Please try again.");
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveJob = async (job: ScrapedJob) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Please sign in to save jobs");
      navigate(`/login?redirect=/app/job-search`);
      return;
    }

    const jobId = job.external_job_id;
    const isSaved = savedJobIds.has(jobId);

    try {
      if (isSaved) {
        await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", user.id)
          .eq("job_id", jobId);
        
        setSavedJobIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
        
        toast.success("Job removed from saved");
      } else {
        const { error } = await supabase.from("saved_jobs").insert({
          user_id: user.id,
          job_id: jobId,
          title: job.title,
          company: job.company,
          location: job.location,
          posted_date: job.posted_at,
          job_type: job.contract_type || 'Full-time',
          remote: job.work_type || 'On-site',
          description: job.description || "",
          url: job.job_url,
          salary: job.salary,
          logo: job.logo
        });

        if (error) throw error;
        
        setSavedJobIds(prev => new Set(prev).add(jobId));
        toast.success("Job saved!");
      }
    } catch (error: any) {
      console.error("Error saving job:", error);
      if (error.code === '23505') {
        toast.error("Job already saved");
      } else {
        toast.error("Failed to save job");
      }
    }
  };

  const handleSmartApplyClick = async (job: ScrapedJob) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please sign in to use Smart Apply');
      navigate('/login?redirect=/app/job-search');
      return;
    }
    
    const added = await addToSmartApply({
      job_id: job.external_job_id,
      title: job.title,
      company: job.company,
      location: job.location,
      description: job.description,
      url: job.apply_url || job.job_url,
      salary: job.salary,
      remote: job.work_type,
      job_type: job.contract_type,
      logo: job.logo,
    });
    
    if (added) {
      await fetchQueueStats();
      navigate('/app/smart-apply/queue');
    }
  };

  const handleJobCardClick = (job: ScrapedJob) => {
    const selectedJob: SelectedJob = {
      id: job.external_job_id,
      title: job.title,
      company: job.company,
      location: job.location,
      salary: job.salary,
      description: job.description,
      url: job.apply_url || job.job_url,
      source: 'apify_linkedin',
      apply_type: 'EASY_APPLY',
      posted_at: job.posted_at,
      logo: job.logo,
    };
    setSelectedJobForModal(selectedJob);
    setShowJobDetailModal(true);
  };

  const clearFilters = () => {
    setDateFilter("any");
    setTypeFilter("any");
    setLocationQuery("Remote");
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Job Search</h1>
            <p className="text-muted-foreground">Search Easy Apply jobs from LinkedIn</p>
          </div>
          <Button onClick={() => navigate('/app/smart-apply/queue')} variant="outline" className="gap-2">
            Smart Apply Queue
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Search className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Jobs Found</p>
                <p className="text-2xl font-bold">{jobs.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent/10 rounded-lg">
                <Bookmark className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Saved Jobs</p>
                <p className="text-2xl font-bold">{savedJobIds.size}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Applied</p>
                <p className="text-2xl font-bold">{appliedCount}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ready to Apply</p>
                <p className="text-2xl font-bold">{readyCount}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Chrome Extension Banner */}
        <ChromeExtensionPrompt />

        {/* Search Section */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Job Keywords</label>
                <Input
                  placeholder="e.g., Software Engineer, Product Manager..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Location</label>
                <CityAutocomplete 
                  value={locationQuery}
                  onChange={setLocationQuery}
                  placeholder="Remote"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Date Posted</label>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any time</SelectItem>
                    <SelectItem value="24h">Last 24 hours</SelectItem>
                    <SelectItem value="week">Last week</SelectItem>
                    <SelectItem value="month">Last month</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Job Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any type</SelectItem>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end gap-2">
                <Button onClick={handleSearch} className="flex-1 gap-2" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Search
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Recent Jobs Section - Only show if not searching */}
        {!hasSearched && !isLoading && (
          <RecentEasyApplyJobs />
        )}

        {/* Results Section */}
        {hasSearched && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                Results for "{searchQuery}"
              </h2>
            {hasSearched && (
              <Badge variant="secondary" className="gap-1">
                <Zap className="h-3 w-3" />
                All Easy Apply • FREE Search
              </Badge>
            )}
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              <p className="text-muted-foreground text-lg font-medium">
                {searchStatus || "Searching..."}
              </p>
              {searchStatus.includes("LinkedIn") && (
                <p className="text-sm text-muted-foreground/70">
                  This may take 1-2 minutes for fresh results
                </p>
              )}
            </div>
          ) : jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job) => (
                <JobCard
                  key={job.external_job_id}
                  job={{
                    id: job.external_job_id,
                    title: job.title,
                    company: job.company,
                    location: job.location,
                    salary: job.salary,
                    description: job.description,
                    job_url: job.apply_url || job.job_url,
                    posted_at: job.posted_at,
                    source: 'apify_linkedin',
                    apply_type: 'EASY_APPLY',
                    saved: savedJobIds.has(job.external_job_id),
                    job_type: job.contract_type,
                    remote: job.work_type,
                    logo: job.logo,
                  }}
                  onSmartApply={() => handleSmartApplyClick(job)}
                  onSave={() => handleSaveJob(job)}
                  onClick={() => handleJobCardClick(job)}
                />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <div className="flex flex-col items-center gap-4 text-muted-foreground">
                <Search className="h-12 w-12" />
                <div>
                  <p className="text-lg font-medium">No jobs found</p>
                  <p className="text-sm">Try different keywords or adjust your filters</p>
                </div>
              </div>
            </Card>
          )}
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      <JobDetailModal
        job={selectedJobForModal}
        open={showJobDetailModal}
        onOpenChange={setShowJobDetailModal}
        onSmartApply={(job) => {
          handleSmartApplyClick({
            external_job_id: job.id,
            title: job.title,
            company: job.company,
            location: job.location,
            salary: job.salary,
            description: job.description,
            job_url: job.url,
            source: job.source,
            apply_type: job.apply_type,
            posted_at: job.posted_at,
            logo: job.logo,
          } as ScrapedJob);
        }}
        onSave={(job) => {
          handleSaveJob({
            external_job_id: job.id,
            title: job.title,
            company: job.company,
            location: job.location,
            salary: job.salary,
            description: job.description,
            job_url: job.url,
            source: job.source,
            apply_type: job.apply_type,
            posted_at: job.posted_at,
            logo: job.logo,
          } as ScrapedJob);
        }}
      />
    </DashboardLayout>
  );
}
